import { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import ConsoleStats from './ConsoleStats';

interface Message {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: Date;
  confidence?: number;
  sources?: Array<{
    source: string;
    content: string;
    relevance_score: number;
    metadata?: any;
  }>;
}

interface ConsolePanelProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function ConsolePanel({ isOpen, onClose }: ConsolePanelProps) {
  const location = useLocation();

  // Detect user role from current route
  const getUserRole = (): string => {
    const path = location.pathname;
    if (path === '/holdco' || path === '/') return 'executive';
    if (path === '/panago') return 'panago';
    if (path === '/phantom') return 'phantom';
    if (path === '/apex') return 'apex';
    return 'executive'; // Default to executive view
  };

  // Get API key for user role (Kenbri Group)
  const getAPIKey = (role: string): string => {
    const API_KEYS: Record<string, string> = {
      'executive': 'rooke_executive_demo_key',
      'panago': 'rooke_panago_demo_key',
      'phantom': 'rooke_phantom_demo_key',
      'apex': 'rooke_apex_demo_key',
    };
    return API_KEYS[role] || API_KEYS['executive'];
  };

  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      role: 'system',
      content: 'Eclipse AI initialized. Connected to Kenbri Group portfolio data with lexical search, semantic search, and ML pattern detection.',
      timestamp: new Date()
    }
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: input,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    const currentInput = input;
    setInput('');
    setIsLoading(true);

    try {
      // Use port 3001 for Rooke Eclipse API (proxied through vite in dev)
      const API_URL = import.meta.env.VITE_API_URL || '';
      const userRole = getUserRole();

      const response = await fetch(`${API_URL}/api/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': getAPIKey(userRole),
        },
        body: JSON.stringify({
          message: currentInput,
          tenant: userRole,
          use_context: true,
          max_sources: 5,
        }),
      });

      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }

      const data = await response.json();

      const aiResponse: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: data.response,
        timestamp: new Date(),
        confidence: data.confidence || 0.92,
        sources: data.sources || []
      };

      setMessages(prev => {
        const updated = [...prev, aiResponse];
        saveStatsToLocalStorage(updated);
        return updated;
      });
    } catch (error) {
      console.error('Chat error:', error);

      const aiResponse: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: `⚠️ AI service temporarily unavailable. Please try again.`,
        timestamp: new Date(),
        confidence: 0.50,
        sources: []
      };

      setMessages(prev => {
        const updated = [...prev, aiResponse];
        saveStatsToLocalStorage(updated);
        return updated;
      });
    } finally {
      setIsLoading(false);
    }
  };

  const saveStatsToLocalStorage = (msgs: Message[]) => {
    const stats = {
      totalMessages: msgs.length - 1, // -1 for system message
      userMessages: msgs.filter(m => m.role === 'user').length,
      aiMessages: msgs.filter(m => m.role === 'assistant').length,
      averageConfidence:
        msgs.filter(m => m.role === 'assistant' && m.confidence).length > 0
          ? msgs.filter(m => m.role === 'assistant').reduce((sum, m) => sum + (m.confidence || 0), 0) /
            msgs.filter(m => m.role === 'assistant').length
          : 0,
      totalSources: msgs.reduce((sum, m) => sum + (m.sources?.length || 0), 0),
      lastUpdated: new Date().toISOString(),
    };
    localStorage.setItem('consoleStats', JSON.stringify(stats));

    // Also save to IndexedDB for persistence and CLI access
    if (window.indexedDB) {
      try {
        const request = indexedDB.open('rooke-stats', 1);
        request.onupgradeneeded = (event) => {
          const db = (event.target as IDBOpenDBRequest).result;
          if (!db.objectStoreNames.contains('console')) {
            db.createObjectStore('console');
          }
        };
        request.onsuccess = (event) => {
          const db = (event.target as IDBOpenDBRequest).result;
          const transaction = db.transaction(['console'], 'readwrite');
          const store = transaction.objectStore('console');
          store.put(stats, 'consoleStats');
        };
      } catch (error) {
        console.error('Failed to save to IndexedDB:', error);
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const handleClearChat = () => {
    setMessages([
      {
        id: '1',
        role: 'system',
        content: 'Chat cleared. Eclipse AI ready. Connected to Kenbri Group portfolio data.',
        timestamp: new Date()
      }
    ]);
  };

  // Quick actions archived - users will type queries directly

  if (!isOpen) return null;

  return (
    <div className="fixed bottom-0 right-0 left-0 md:left-64 z-50 transition-transform duration-300">
      {/* Panel */}
      <div className="bg-white border-t-2 border-blue-700 shadow-2xl" style={{ height: '60vh' }}>
        {/* Header */}
        <div className="bg-blue-700 text-white px-6 py-3 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="text-2xl">💬</div>
            <div>
              <h3 className="font-semibold">Console</h3>
              <p className="text-xs text-blue-200">Eclipse AI Assistant</p>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2 text-xs">
              <div className="w-2 h-2 bg-green-400 rounded-full"></div>
              <span>Connected</span>
            </div>

            {/* Clear Chat Button */}
            <button
              onClick={handleClearChat}
              title="Clear chat history"
              className="text-white hover:bg-blue-800 rounded p-1 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            <button
              onClick={onClose}
              className="ml-2 text-white hover:bg-blue-800 rounded p-1 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          </div>
        </div>

        {/* Messages */}
        <div className="flex flex-col" style={{ height: 'calc(60vh - 120px)' }}>
          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {messages.map((message) => (
              <MessageBubble key={message.id} message={message} />
            ))}
            {isLoading && (
              <div className="flex items-start space-x-2">
                <div className="flex-shrink-0 w-7 h-7 rounded-full bg-blue-700 flex items-center justify-center text-white text-xs font-medium">
                  AI
                </div>
                <div className="flex-1 bg-gray-100 rounded-lg p-3">
                  <div className="flex items-center space-x-1">
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Stats Section - Compact single line */}
          {messages.length > 1 && (
            <div className="border-t border-gray-200 px-3 py-1 bg-gray-50">
              <ConsoleStats
                totalMessages={messages.length - 1}
                userMessages={messages.filter(m => m.role === 'user').length}
                aiMessages={messages.filter(m => m.role === 'assistant').length}
                averageConfidence={
                  messages.filter(m => m.role === 'assistant' && m.confidence).length > 0
                    ? messages.filter(m => m.role === 'assistant').reduce((sum, m) => sum + (m.confidence || 0), 0) / messages.filter(m => m.role === 'assistant').length
                    : 0
                }
                totalSources={messages.reduce((sum, m) => sum + (m.sources?.length || 0), 0)}
              />
            </div>
          )}

          {/* Input Area */}
          <div className="border-t border-gray-200 p-2 bg-gray-50">
            <form onSubmit={handleSubmit} className="flex items-end space-x-2">
              <div className="flex-1">
                <textarea
                  ref={inputRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Ask about Panago, Phantom Screens, Apex, revenue, margins..."
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600 resize-none"
                  rows={2}
                  disabled={isLoading}
                />
              </div>
              <button
                type="submit"
                disabled={!input.trim() || isLoading}
                className="px-4 py-2 bg-blue-700 text-white rounded hover:bg-blue-800 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors font-medium text-sm"
              >
                Send
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

function MessageBubble({ message }: { message: Message }) {
  if (message.role === 'system') {
    return (
      <div className="flex justify-center">
        <div className="bg-blue-50 border border-blue-200 rounded px-3 py-1.5 text-xs text-blue-800">
          <div className="flex items-center space-x-2">
            <span>ℹ️</span>
            <span>{message.content}</span>
          </div>
        </div>
      </div>
    );
  }

  if (message.role === 'user') {
    return (
      <div className="flex items-start space-x-2 justify-end">
        <div className="flex-1">
          <div className="bg-blue-700 text-white rounded-lg p-3">
            <p className="text-sm whitespace-pre-wrap">{message.content}</p>
          </div>
          <p className="text-xs text-gray-500 mt-1 text-right">
            {message.timestamp.toLocaleTimeString()}
          </p>
        </div>
        <div className="flex-shrink-0 w-7 h-7 rounded-full bg-gray-300 flex items-center justify-center text-gray-700 text-xs font-medium">
          U
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-start space-x-2">
      <div className="flex-shrink-0 w-7 h-7 rounded-full bg-blue-700 flex items-center justify-center text-white text-xs font-medium">
        AI
      </div>
      <div className="flex-1">
        <div className="bg-gray-100 rounded-lg p-3">
          <div className="text-sm text-gray-800 prose prose-sm max-w-none">
            <ReactMarkdown>{message.content}</ReactMarkdown>
          </div>

          {message.confidence && (
            <div className="mt-2 pt-2 border-t border-gray-200">
              <div className="flex items-center justify-between text-xs">
                <span className="text-gray-600">Confidence</span>
                <span className="font-semibold text-green-600">
                  {(message.confidence * 100).toFixed(0)}%
                </span>
              </div>
              <div className="mt-1 bg-gray-200 rounded-full h-1">
                <div
                  className="bg-green-500 h-1 rounded-full"
                  style={{ width: `${message.confidence * 100}%` }}
                />
              </div>
            </div>
          )}

          {message.sources && message.sources.length > 0 && (
            <div className="mt-2 pt-2 border-t border-gray-200">
              <p className="text-xs font-medium text-gray-600 mb-1">Sources:</p>
              <div className="space-y-1">
                {message.sources.map((src, idx) => (
                  <div key={idx} className="flex items-center justify-between text-xs">
                    <span className="text-gray-700 truncate max-w-xs">
                      {src.source}
                    </span>
                    <span className="text-gray-500 ml-2 flex-shrink-0">
                      {(src.relevance_score * 100).toFixed(0)}%
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <p className="text-xs text-gray-500 mt-1">
          {message.timestamp.toLocaleTimeString()}
        </p>
      </div>
    </div>
  );
}
